import { PiCheck } from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Badge } from 'src/modules/shared/components/atoms/badge';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { formatTimeDistance } from 'src/modules/shared/utils/formatting/date.util';
import { NotificationInboxItem } from '../../types/notification.types';
import { formatNotificationChannel } from '../../utils/notification-format.util';

interface NotificationListItemProps {
  item: NotificationInboxItem;
  onClick: (item: NotificationInboxItem) => void;
  onMarkRead?: (item: NotificationInboxItem) => void;
  markReadDisabled?: boolean;
}

export const NotificationListItem: React.FC<NotificationListItemProps> = ({
  item,
  onClick,
  onMarkRead,
  markReadDisabled = false,
}) => {
  const isUnread = !item.readAt;
  const createdAt = new Date(item.createdAt);
  const timeLabel = formatTimeDistance(createdAt, true);
  const links = item.metadata?.links ?? [];
  const deepLink = item.metadata?.deepLink;
  const linkItems =
    links.length > 0
      ? links
      : deepLink
        ? [{ label: 'Abrir enlace', url: deepLink }]
        : [];

  const handleMarkRead = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onMarkRead?.(item);
  };

  const handleOpenLink = (
    event: React.MouseEvent<HTMLButtonElement>,
    url: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border transition-colors ${
        isUnread
          ? 'border-primary-100 bg-primary-50'
          : 'border-neutral-200 bg-white'
      }`}
    >
      <button
        type="button"
        className="flex-1 text-left px-4 py-3"
        onClick={() => onClick(item)}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Text weight="medium" className="text-sm text-primary-900">
                {item.title}
              </Text>
              {isUnread && (
                <span className="h-2 w-2 rounded-full bg-primary-500" />
              )}
            </div>
            <Text className="text-xs text-secondary-600 line-clamp-2">
              {item.body}
            </Text>
            {linkItems.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {linkItems.map((link, index) => (
                  <Button
                    key={`${link.url}-${index}`}
                    variant="secondary"
                    appearance="outline"
                    size="xs"
                    onClick={(event) => handleOpenLink(event, link.url)}
                  >
                    {link.label}
                  </Button>
                ))}
              </div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <Badge size="xs" appearance="soft" variant="secondary">
                {formatNotificationChannel(item.channel)}
              </Badge>
              <Text className="text-[11px] text-secondary-500">
                {timeLabel}
              </Text>
            </div>
          </div>
        </div>
      </button>
      {onMarkRead && (
        <div className="pt-3 pr-3">
          <Button
            appearance="ghost"
            variant="secondary"
            size="xs"
            isIconOnly
            onClick={handleMarkRead}
            disabled={!isUnread || markReadDisabled}
            title={isUnread ? 'Marcar como leído' : 'Leído'}
            aria-label={isUnread ? 'Marcar como leído' : 'Leído'}
          >
            <PiCheck
              className={`h-4 w-4 ${
                isUnread ? 'text-primary-600' : 'text-secondary-300'
              }`}
            />
          </Button>
        </div>
      )}
    </div>
  );
};
